import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, Bell, BellOff } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/** Header-mounted button + Dialog for per-user email mute. Phase 3.
 *  Defaults to ON server-side; flipping it persists via PATCH /me/preferences
 *  and refreshes user state through AuthContext. */
export const EmailSettingsButton = () => {
  const { user, checkAuth } = useAuth();
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(user?.email_notifications_enabled !== false);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    setEnabled(user?.email_notifications_enabled !== false);
  }, [user?.email_notifications_enabled]);

  const submit = async (next) => {
    setSaving(true);
    try {
      await api.patch("/me/preferences", { email_notifications_enabled: next });
      setEnabled(next);
      toast.success(next ? "Email notifications ENABLED" : "Email notifications MUTED");
      checkAuth?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update preference");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <>
      <button
        data-testid="email-settings-btn"
        onClick={() => setOpen(true)}
        className="w-8 h-8 flex items-center justify-center border border-black hover:bg-black hover:text-white transition-colors relative"
        title="Email notifications"
      >
        <Mail size={14} strokeWidth={2} />
        {!enabled && (
          <span
            data-testid="email-muted-dot"
            className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 border border-white"
          />
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="email-settings-modal"
          className="max-w-md  border border-slate-200 p-0 gap-0"
        >
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-200/10">
            <DialogTitle className="font-semibold uppercase tracking-tight flex items-center gap-2">
              <Mail size={16}/> Email Notifications
            </DialogTitle>
            <DialogDescription className="font-mono text-[11px] text-slate-500">
              Mute all transactional emails sent to <span className="font-bold text-black">{user.email}</span>.
              In-app notifications keep working regardless.
            </DialogDescription>
          </DialogHeader>

          <div className="px-5 py-4 space-y-3">
            <div className="border border-slate-200/10 p-4 bg-slate-50">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="font-mono text-[10px] uppercase tracking-widest font-bold text-slate-500">
                    CURRENT STATUS
                  </div>
                  <div className="font-semibold text-xl tracking-tight mt-1 flex items-center gap-2">
                    {enabled ? (
                      <><Bell size={16} className="text-olive"/> ENABLED</>
                    ) : (
                      <><BellOff size={16} className="text-red-600"/> MUTED</>
                    )}
                  </div>
                </div>
                <button
                  data-testid="email-toggle"
                  role="switch"
                  aria-checked={enabled}
                  onClick={() => !saving && submit(!enabled)}
                  disabled={saving}
                  className={`relative w-14 h-8 border border-slate-200 transition-colors disabled:opacity-50 ${
                    enabled ? "bg-olive" : "bg-white"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 w-6 h-6 bg-white border border-black transition-all ${
                      enabled ? "left-6" : "left-0.5"
                    }`}
                  />
                </button>
              </div>
            </div>

            <ul className="font-mono text-[11px] text-slate-600 list-disc pl-5 space-y-1">
              <li>Request raised (approvers)</li>
              <li>Request approved / rejected (you, if you raised it)</li>
              <li>Payment completed (you, if you raised it)</li>
              <li>24h unpaid reminders (accountants)</li>
            </ul>
          </div>

          <DialogFooter className="px-5 pb-5 pt-2">
            <button
              data-testid="email-settings-close"
              onClick={() => setOpen(false)}
              className="w-full border border-slate-200 px-4 py-3 font-mono text-xs uppercase tracking-widest font-bold bg-white hover:bg-slate-100"
            >
              Done
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
